import { Role, UserData } from "@/types/globalTypes";
import { isWeb, mobileAppReview } from "@/utils/helpers";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";


// All route groups
const sharedRoutes = [
  "/login",
  "/register",
  "/reset-password",
  "/activate-account",
  "/forget-password",
  "/accept-invite",
  "/notifications",
  "/settings/user-settings",
];

const ownerAndSuperAdminRoutes = [
  "/dashboard",
  "/orders",
  "/orders/reviews",
  "/products",
  "/cashier",
  "/cashierhistory",
  "/clients",
  "/apps",
  "/reports",
  "/tables",
  "/settings",
  "/settings/general-settings",
  "/settings/theme",
  "/settings/maintenance-settings",
  "/settings/language-settings",
  "/settings/taxes-settings",
  "/settings/technical-support",
  "/settings/coupons",
  "/settings/branches-settings",
  "/settings/subscribtion",
  "/settings/domain-settings",
  "/settings/payment-settings",
  "/settings/verification",
  "/settings/store-balance-history",
  "/settings/store-cash-history",
  "/settings/wallet-settings",
  "/settings/terms-privacy",
  "/settings/qrcode-settings",
  "/settings/permissions",
  "/settings/permissions/add-employee",
  "/settings/permissions/employee-info"
];

const adminRoutes = [
  "/dashboard",
  "/orders",
  "/orders/reviews",
  "/products",
  "/cashier",
  "/cashierhistory",
  "/clients",
  "/reports",
  "/tables",
  "/settings",
  "/settings/branches-settings",
  "/settings/permissions",
  "/settings/permissions/add-employee",
  "/settings/permissions/employee-info"
];

const dynamicRouteMatch = (routePattern: string, pathname: string) => {
  const regex = new RegExp(
    '^' + routePattern.replace(/:[^/]+/g, '[^/]+') + '$'
  );
  return regex.test(pathname);
};

const cashierRoutes = ["/cashier", "/cashierhistory","/orders","/tables"];
const waiterRoutes = ["/cashier","/cashierhistory","/orders","/tables"];
const chefRoutes = ["/chefs-orders","/chefs-orders-history"];
const driverRoutes = ["/driver-orders","/driver-orders-history"];

const roleTranslations  = {
  owner: { en: "Owner", ar: "المالك" },
  superadmin: { en: "Super Admin", ar: "مدير الفروع" },
  admin: { en: "Admin", ar: "مدير الفرع" },
  cashier: { en: "Cashier", ar: 'كاشير' },
  chef: { en: "Chef", ar: 'شيف' },
  driver: { en: "Driver", ar: 'عامل توصيل' },
  waiter: { en: "Waiter", ar: "ويتر" },
};

// Hook return type
interface UseUserRoleResult {
  role: Role;
  RoleNamesEnAr:  { en: string; ar: string } 
  isAdmin: boolean;
  isOwner: boolean;
  isSuperAdmin: boolean;
  isBranchAdmin: boolean;
  isChef: boolean;
  isCashier: boolean;
  isDriver: boolean;
  isWaiter: boolean;
  accessibleRoutes: string[];
  hasPermission: (route: string) => boolean;
  roleRoutes: string[];
  isRoleLoaded: boolean;
  openAppReviewBasedOnRole: () => void
}

export const useUserRole = (): UseUserRoleResult => {
  const [isRoleLoaded, setIsRoleLoaded] = useState(false);

    const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const userRole = useSelector(
    (state: { user: { userData: UserData } }) => state?.user?.userData?.role,
  );
  const userJoinTime = useSelector(
    (state: { user: { userData: UserData } }) => state?.user?.userData?.joinTime,
  );

  const accountCreatedFromInDays = Math.floor((Date.now() - userJoinTime * 1000) / 86400000);


  const isAdmin = userRole === "owner" || userRole === "superadmin";
  const isOwner = userRole === "owner";
  const isSuperAdmin = userRole === "superadmin";
  const isBranchAdmin = userRole === "admin";
  const isCashier = userRole === "cashier";
  const isChef = userRole === "chef";
  const isDriver = userRole === "driver";
  const isWaiter = userRole === "waiter";

  const [accessibleRoutes, setAccessibleRoutes] = useState<string[]>([]);

  const roleRoutes = useMemo(() => {
    if (isAdmin || isOwner || isSuperAdmin) {
      return [...ownerAndSuperAdminRoutes];
    } else if (isBranchAdmin) {
      return [...adminRoutes];
    } else if (isCashier) {
      return [...cashierRoutes];
    } else if (isChef) {
      return [...chefRoutes];
    } else if (isDriver) {
      return [...driverRoutes];
    }else if (isWaiter) {
      return [...waiterRoutes];
    } else {
      return [...sharedRoutes];
    }
  }, [
    isAdmin,
    isBranchAdmin,
    isCashier,
    isChef,
    isDriver,
    isWaiter,
    isOwner,
    isSuperAdmin,
  ]);

  useEffect(() => {
    if(!token) return
    setIsRoleLoaded(false);
    let routes = [...sharedRoutes];

    if (isAdmin || isOwner || isSuperAdmin) {
      routes = [...routes, ...ownerAndSuperAdminRoutes];
    } else if (isBranchAdmin) {
      routes = [...routes, ...adminRoutes];
    } else if (isCashier) {
      routes = [...routes, ...cashierRoutes];
    } else if (isChef) {
      routes = [...routes, ...chefRoutes];
    } else if (isDriver) {
      routes = [...routes, ...driverRoutes];
    }else if (isWaiter) {
      routes = [...routes, ...waiterRoutes];
    } else {
      routes = [...routes];
    }

    setAccessibleRoutes(routes);

    setIsRoleLoaded(true);
  }, [
    userRole,
    isAdmin,
    isBranchAdmin,
    isCashier,
    isChef,
    isDriver,
    isWaiter,
    isOwner,
    isSuperAdmin,
    token
  ]);

const hasPermission = useMemo(
  () => (pathname: string) => {
    // Always return true for privileged roles
    if (isAdmin || isOwner || isSuperAdmin) {
      return true;
    } else if (isBranchAdmin) {
      return (
        accessibleRoutes.includes(pathname) ||
        dynamicRouteMatch("/settings/permissions/employee-info/:id", pathname)||
        dynamicRouteMatch("/clients/:id", pathname)||
        dynamicRouteMatch("/tables/:floorId/:tableId", pathname)
      );
    } else if( isCashier || isWaiter){
      return accessibleRoutes.includes(pathname) || 
      dynamicRouteMatch("/tables/:floorId/:tableId", pathname)
    } else {
      return accessibleRoutes.includes(pathname);
    }
  },
  [isAdmin, isOwner, isSuperAdmin, isBranchAdmin, isCashier, isWaiter, accessibleRoutes]
);

const openAppReviewBasedOnRole =  () => {
  if(isWeb) return
  const isMoreThanOrEqual15Days = accountCreatedFromInDays >= 15 || !accountCreatedFromInDays

  if(isOwner && isMoreThanOrEqual15Days){ 
    // Owner => 15 days + view for reports.
    mobileAppReview()
  }
  else if (isSuperAdmin && isMoreThanOrEqual15Days){ 
    // Branches manger => 15 days + create / edit branch.
    mobileAppReview()
  }
  else if (isBranchAdmin && isMoreThanOrEqual15Days){ 
    // Branch manager => 15 days + view a new order or a review.
    mobileAppReview()
  }
  else if (isCashier || isChef || isDriver || isWaiter){
    /**
      Cashier => After creating an order successfully.
      Chief => After finishing an order successfully.
      Driver => After delivering on order successfully.
      Waiter => After creating an order successfully.
    */
    mobileAppReview()
  }
  
}


  return {
    role: userRole,
    RoleNamesEnAr :{en:roleTranslations[userRole]?.en,ar:roleTranslations[userRole]?.ar},
    isAdmin,
    isOwner,
    isSuperAdmin,
    isBranchAdmin,
    isCashier,
    isChef,
    isDriver,
    isWaiter,
    accessibleRoutes,
    roleRoutes,
    hasPermission,
    isRoleLoaded,
    openAppReviewBasedOnRole
  };
};
